import React, {useCallback, useContext, useEffect, useRef, useState} from 'react';
import axios from "axios";
import {Tag, Form, Input, Select} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import TableFilterControlsForm from "@common/TableFilterControlsForm";
import TableFilterControls from "@common/TableFilterControls";
import TableFilterControlsSection from "@common/TableFilterControlsSection";
import Tooltip from "@common/Tooltip";
import Button from "@common/Button";
import {AddModeratorRounded, DeleteRounded, EditRounded, RemoveModeratorRounded} from "@mui/icons-material";
import RemoveUserFromSchoolDialog from "./components/RemoveUserFromSchoolDialog";
import AddUserAdminPermsDialog from "./components/AddUserAdminPermsDialog";
import RemoveUserAdminPermsDialog from "./components/RemoveUserAdminPermsDialog";
import {UserContext} from "@context/UserContext";
import EditUserEmailDialog from "./components/EditUserEmailDialog";
import ExportToCsvButton from "@common/ExportToCsvButton";
import TableAntD from "@common/TableAntD";
import {useScreenReaderAnnouncement} from "@hooks/useScreenReaderAnnouncment";
import ViewUserProductsDialog from "./components/ViewUserProductsDialog";
import PageHeader from "@common/PageHeader";

const { Option } = Select;

const SchoolUsers = () => {
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [selectedRole, setSelectedRole] = useState('STUDENT');
    const [isRemoveUserDialogVisible, setRemoveUserDialogVisible] = useState(false);
    const [isAddAdminPermsDialogVisible, setAddAdminPermsDialogVisible] = useState(false);
    const [isRemoveAdminPermsDialogVisible, setRemoveAdminPermsDialogVisible] = useState(false);
    const [isEditUserEmailDialogVisible, setEditUserEmailDialogVisible] = useState(false);
    const [isViewUserProductsDialogVisible, setIsViewUserProductsDialogVisible] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [isExporting, setIsExporting] = useState(false);
    const [form] = Form.useForm();
    const [refetchTrigger, setRefetchTrigger] = useState(0);
    const {user} = useContext(UserContext);
    const { setSrAnnouncement, LiveRegion, srAnnouncement } = useScreenReaderAnnouncement();

    useEffect(() => {
        form.setFieldsValue({ role: selectedRole });
    }, [selectedRole, form]);

    const handleRoleChange = (value) => {
        setSelectedRole(value);
        setRefetchTrigger(prev => prev + 1);
    };

    const debounceTimerRef = useRef(null);

    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        };
    }, []);

    const handleSearchChange = useCallback(() => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        debounceTimerRef.current = setTimeout(() => {
            setRefetchTrigger(prev => prev + 1);
        }, 400);
    }, []);

    const formatLastActive = (timestamp) => {
        if (!timestamp) return 'Never';
        return new Date(timestamp).toLocaleDateString();
    };

    const handleRemoveUserButtonClick = (user) => {
        setSelectedUser(user);
        setRemoveUserDialogVisible(true);
    };

    const handleRemoveUserPermissionsClick = (user) => {
        setSelectedUser(user);
        setRemoveAdminPermsDialogVisible(true);
    }

    const handleAddUserPermissionsClick = (user) => {
        setSelectedUser(user);
        setAddAdminPermsDialogVisible(true);
    }

    const handleEditUserEmailClick = (user) => {
        setSelectedUser(user);
        setEditUserEmailDialogVisible(true);
    }

    const handleViewSubscriptionsClick = (user) => {
        setSelectedUser(user);
        setIsViewUserProductsDialogVisible(true);
    }

    const handleDialogClose = (refetchData) => {
        setRemoveUserDialogVisible(false);
        setAddAdminPermsDialogVisible(false);
        setRemoveAdminPermsDialogVisible(false);
        setEditUserEmailDialogVisible(false);
        setIsViewUserProductsDialogVisible(false);
        setSelectedUser(null);

        if (refetchData) {
            setRefetchTrigger(prev => prev + 1);
        }
    };

    const fetchAllUsersForExport = async () => {
        setIsExporting(true);
        try {
            const response = await axios.get('/web/ui/sc/users');

            // Convert object of objects to array
            const usersObject = response.data || {};
            let allUsers = Object.values(usersObject);

            // Filter enabled users only
            allUsers = allUsers.filter(u => u.enabled !== false);

            // Map to export format
            const exportData = allUsers.map(user => ({
                fullName: user.fullName,
                email: user.email,
                subscriberType: user.subscriberType?.toLowerCase(),
                lastAccessedDate: user.lastAccessedDate ? formatLastActive(user.lastAccessedDate) : 'Never logged in'
            }));

            return exportData;
        } catch (error) {
            console.error('Error fetching all users for export:', error);
            setSrAnnouncement('Error exporting users. Please try again.');
            return [];
        } finally {
            setIsExporting(false);
        }
    };

    const fetchData = (pagingParams, handleTableData, handlePagingData, abortController) => {
        const formValues = form.getFieldsValue();
        const searchValue = formValues.search || '';

        const endpoint = selectedRole === 'STUDENT' ? '/web/ui/sc/schoolStudents' : '/web/ui/sc/schoolTeachers';

        const params = { page: pagingParams.pageNumber };
        if (searchValue.trim()) {
            params.search = searchValue.trim();
        }

        axios.get(endpoint, {
            params,
            signal: abortController.signal
        }).then(response => {
            let users = response.data.currPageContents || [];
            const totalResults = response.data.totalResults || 0;

            // Filter enabled users only
            users = users.filter(u => u.enabled !== false);

            handleTableData(users);
            setFilteredUsers(users);
            handlePagingData({
                pageSize: pagingParams.pageSize,
                totalResults: totalResults,
            });

            const resultMessage = totalResults === 1 ? `1 user` : `${totalResults} users`;
            setSrAnnouncement(`Table updated. ${resultMessage} found.`);
        })
            .catch(error => {
                if (error.name !== 'CanceledError') {
                    console.error('Error fetching users:', error);
                    handleTableData([]);
                    setFilteredUsers([]);
                    handlePagingData({
                        pageSize: pagingParams.pageSize,
                        totalResults: 0
                    });
                    setSrAnnouncement(`Error loading users. Please try again.`);
                }
            });
    };

    const columns = [
        {
            title: 'Full Name',
            dataIndex: 'fullName',
            key: 'fullName',
            render: (value, record) => <span style={{fontWeight: 600}}>{value}</span>,
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
        },
        {
            title: 'Role',
            dataIndex: 'subscriberType',
            key: 'subscriberType',
            render: text => (
                <Tag color={text === 'STUDENT' ? 'purple' : 'green'} variant={'outlined'} style={{textTransform: 'capitalize', fontSize: 14, padding: '4px 16px'}}>
                    {text?.toLowerCase()}
                </Tag>
            ),
        },
        {
            title: 'Products',
            dataIndex: 'products',
            key: 'products',
            render: (value = [], record) => {
                if (value.length < 1) return 'No products assigned';
                const productName = value[0]?.name || '';

                return (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            minWidth: 0,
                        }}
                    >
                        <Tooltip title={productName}>
                            <span
                                style={{
                                    flex: 1,
                                    minWidth: 0,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    whiteSpace: 'normal',
                                    lineHeight: '1.3em',
                                }}
                            >
                                {productName}
                            </span>
                        </Tooltip>
                        {value.length > 1 &&
                            <Tooltip title={`${value.length - 1} more products`}>
                                <Button
                                    type={'outline'}
                                    aria-label={`${value.length - 1} more products`}
                                    onClick={() => handleViewSubscriptionsClick(record)}
                                >
                                    +{value.length - 1}
                                </Button>
                            </Tooltip>
                        }
                    </div>
                );
            }
        },
        {
            title: 'Last Active',
            dataIndex: 'lastAccessedDate',
            key: 'lastActive',
            render: (timestamp) => formatLastActive(timestamp),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => {
                if (record.userSource !== 'EDJIN') {
                    return null;
                }

                return (
                    <div style={{ display: 'flex'}}>
                        <Tooltip title={'Edit user email'}>
                            <Button type={'text'} onClick={() => handleEditUserEmailClick(record)} aria-label={'Edit user email'}><EditRounded /></Button>
                        </Tooltip>
                        {(record.subscriberType === 'TEACHER' && !record.schoolAdmin) &&
                            <Tooltip title={'Make School Administrator'}>
                                <Button type={'text'} onClick={() => handleAddUserPermissionsClick(record)} aria-label={'Make Administrator'}><AddModeratorRounded /></Button>
                            </Tooltip>
                        }
                        {(record.subscriberType === 'TEACHER' && record.schoolAdmin) &&
                            <Tooltip title={'Remove School Administrator Permission'}>
                                <Button type={'text'} onClick={() => handleRemoveUserPermissionsClick(record)} aria-label={'Remove Administrator Permission'}><RemoveModeratorRounded /></Button>
                            </Tooltip>
                        }
                        <Tooltip title={'Remove user from school'}>
                            <Button type={'text'} onClick={() => handleRemoveUserButtonClick(record)} aria-label={'Delete user from school'}><DeleteRounded style={{color: 'red'}} /></Button>
                        </Tooltip>
                    </div>
                );
            },
        },
    ].filter(column => {
        if (column.key === 'actions') {
            if (!user.schoolAdmin) {
                return false;
            }
            return filteredUsers.some(u => u.userSource === 'EDJIN');
        }
        return true;
    });

    const exportColumns = columns.filter(col => col.key !== 'actions' && col.key !== 'products');

    return (
        <div>
            <LiveRegion message={srAnnouncement} />
            <PageHeader
                title={'Users'}
                buttons={
                    <>
                        <ExportToCsvButton
                            fetchDataForExport={fetchAllUsersForExport}
                            columns={exportColumns}
                            fileName="school_users.csv"
                            disabled={isExporting}
                            buttonText={isExporting ? "Exporting..." : "Export Users"}
                        />
                    </>
                }
            />
            <TableFilterControls
                filterContent={
                    <TableFilterControlsForm form={form}>
                        <TableFilterControlsSection showDivider={false}>
                            <Form.Item name="search" label={'User name or email'}>
                                <Input
                                    prefix={<SearchOutlined/>}
                                    onChange={handleSearchChange}
                                    allowClear
                                />
                            </Form.Item>

                            <Form.Item name="role" label={'Role'}>
                                <Select
                                    value={selectedRole}
                                    onChange={handleRoleChange}
                                >
                                    <Option value="STUDENT">Student</Option>
                                    <Option value="TEACHER">Teacher</Option>
                                </Select>
                            </Form.Item>
                        </TableFilterControlsSection>
                    </TableFilterControlsForm>
                }
            />

            <TableAntD
                dynamicWidth={true}
                columnData={columns}
                pagingDependencies={[refetchTrigger, selectedRole]}
                pagingData={{
                    fetchFunction: fetchData,
                    params: { pageSize: 20 }
                }}
                rowKey="userId"
                locale={{
                    emptyText: <span className="ch-warning-text">No users found</span>
                }}
            />
            <RemoveUserFromSchoolDialog
                visible={isRemoveUserDialogVisible}
                user={selectedUser}
                handleClose={handleDialogClose}
            />
            <AddUserAdminPermsDialog
                visible={isAddAdminPermsDialogVisible}
                user={selectedUser}
                handleClose={handleDialogClose}
            />
            <RemoveUserAdminPermsDialog
                visible={isRemoveAdminPermsDialogVisible}
                user={selectedUser}
                handleClose={handleDialogClose}
            />
            <EditUserEmailDialog
                visible={isEditUserEmailDialogVisible}
                user={selectedUser}
                handleClose={handleDialogClose}
            />
            <ViewUserProductsDialog
                visible={isViewUserProductsDialogVisible}
                user={selectedUser}
                handleClose={handleDialogClose}
            />
        </div>
    );
};

export default SchoolUsers;